import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, RefreshCw, Mail, MessageCircle, CheckCircle2, XCircle, AlertTriangle, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_META = {
  sent:              { label: "Trimis",              cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", icon: CheckCircle2 },
  send_error:        { label: "Eroare la trimitere", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300",           icon: XCircle },
  no_recipient:      { label: "Fără destinatar",     cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",         icon: AlertTriangle },
  config_disabled:   { label: "Config dezactivat",   cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",         icon: AlertTriangle },
  no_template:       { label: "Fără template",       cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",         icon: AlertTriangle },
  template_missing:  { label: "Template lipsă",      cls: "border-rose-500/40 bg-rose-500/10 text-rose-300",             icon: XCircle },
  not_approved:      { label: "Neaprobat Meta",      cls: "border-rose-500/40 bg-rose-500/10 text-rose-300",             icon: XCircle },
};

const FLOW_LABEL = { garantie: "Garanție", retur: "Retur", schimb: "Schimb", livrare: "Livrare" };

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

/**
 * Global notification log viewer.
 * Fetches `/api/returns/warranty/notif-log` and lets the admin filter by
 * channel, outcome, request number, or return id. Each row shows the exact
 * reason a notification was NOT sent (or the WhatsApp msg id when sent).
 */
export default function NotificationLogModal({ onClose, initialRequestNumber }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({
    channel: "",
    outcome: "",
    request_number: initialRequestNumber || "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ limit: "300" });
      if (filters.channel) params.set("channel", filters.channel);
      if (filters.outcome) params.set("outcome", filters.outcome);
      if (filters.request_number.trim()) params.set("request_number", filters.request_number.trim());
      const r = await axios.get(`${API}/returns/warranty/notif-log?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Eroare încărcare");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const errorCount = items.filter((i) => !["sent"].includes(i.outcome)).length;
  const okCount = items.filter((i) => i.outcome === "sent").length;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4" data-testid="notif-log-modal">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/15 text-sky-300">
              <Search className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-100">Jurnal Notificări — Email + WhatsApp</h2>
              <p className="text-[11px] text-zinc-500">
                Fiecare încercare (succes / eșec / omisă). Persistă chiar dacă ștergi cererea.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500">
              <span className="text-emerald-400">{okCount} OK</span> · <span className="text-rose-400">{errorCount} probleme</span>
            </span>
            <button data-testid="notif-log-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-200 hover:border-zinc-600 disabled:opacity-40">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button data-testid="notif-log-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 bg-zinc-900/40 px-5 py-3 text-xs">
          <select
            data-testid="notif-log-filter-channel"
            value={filters.channel}
            onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}
            className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-zinc-100"
          >
            <option value="">Toate canalele</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <select
            data-testid="notif-log-filter-outcome"
            value={filters.outcome}
            onChange={(e) => setFilters((f) => ({ ...f, outcome: e.target.value }))}
            className="rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-zinc-100"
          >
            <option value="">Toate rezultatele</option>
            <option value="sent">✅ Trimis</option>
            <option value="send_error">❌ Eroare la trimitere</option>
            <option value="no_template">⚠️ Fără template</option>
            <option value="template_missing">❌ Template lipsă din DB</option>
            <option value="not_approved">❌ Neaprobat Meta (WhatsApp)</option>
            <option value="no_recipient">⚠️ Fără destinatar</option>
            <option value="config_disabled">⚠️ Config global dezactivat</option>
          </select>
          <input
            data-testid="notif-log-filter-number"
            type="text"
            placeholder="Nr. cerere (ex. 0033)"
            value={filters.request_number}
            onChange={(e) => setFilters((f) => ({ ...f, request_number: e.target.value }))}
            className="w-40 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 font-mono text-zinc-100 placeholder:text-zinc-600"
          />
          <button
            onClick={() => setFilters({ channel: "", outcome: "", request_number: "" })}
            className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-300"
          >
            Resetează filtre
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && <div className="py-12 text-center text-sm text-zinc-500">Se încarcă…</div>}
          {err && !loading && <div className="mx-5 my-4 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">{err}</div>}
          {!loading && !err && items.length === 0 && (
            <div className="py-16 text-center text-sm text-zinc-500">
              Niciun jurnal pentru filtrele aplicate. Fă o schimbare de status pentru o cerere ca să vezi înregistrări noi.
            </div>
          )}
          {!loading && !err && items.length > 0 && (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="w-[130px] px-3 py-2.5 text-left font-medium">Timp</th>
                  <th className="w-[80px] px-3 py-2.5 text-left font-medium">Cerere</th>
                  <th className="w-[85px] px-3 py-2.5 text-left font-medium">Flow / Pas</th>
                  <th className="w-[95px] px-3 py-2.5 text-left font-medium">Canal</th>
                  <th className="w-[140px] px-3 py-2.5 text-left font-medium">Rezultat</th>
                  <th className="px-3 py-2.5 text-left font-medium">Detalii</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const meta = OUTCOME_META[row.outcome] || { label: row.outcome, cls: "border-zinc-600/40 bg-zinc-800/40 text-zinc-300", icon: AlertTriangle };
                  const Icon = meta.icon;
                  const isEmail = row.channel === "email";
                  return (
                    <tr key={row.id || `${row.at}-${row.channel}-${row.status_key}`} className="border-t border-zinc-900 hover:bg-zinc-900/30" data-testid={`notif-log-row-${row.outcome}`}>
                      <td className="px-3 py-2 align-top text-[11px] font-mono text-zinc-400">{fmtTime(row.at)}</td>
                      <td className="px-3 py-2 align-top text-[11px] font-mono text-zinc-100">#{row.request_number || "—"}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-[11px] text-zinc-100">{FLOW_LABEL[row.flow] || row.flow}</div>
                        <div className="mt-0.5 font-mono text-[9px] text-zinc-600">{row.status_key}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                          isEmail ? "border-blue-500/40 bg-blue-500/10 text-blue-300"
                                  : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        }`}>
                          {isEmail ? <Mail className="h-3 w-3" /> : <MessageCircle className="h-3 w-3" />}
                          {isEmail ? "Email" : "WhatsApp"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.cls}`}>
                          <Icon className="h-3 w-3" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="space-y-0.5 text-[11px]">
                          {row.recipient && <div className="font-mono text-zinc-400">→ {row.recipient}</div>}
                          {row.template_name && <div className="text-zinc-300">Template: <span className="font-medium">{row.template_name}</span></div>}
                          {row.subject && <div className="truncate text-zinc-500">Subject: {row.subject}</div>}
                          {row.error && <div className="whitespace-pre-wrap rounded border border-rose-500/20 bg-rose-500/5 px-1.5 py-1 font-mono text-[10px] text-rose-200">{row.error}</div>}
                          {row.meta_status && row.meta_status !== "APPROVED" && (
                            <div className="text-amber-300">Meta status: <span className="font-mono">{row.meta_status}</span></div>
                          )}
                          {row.extra?.wa_id && <div className="font-mono text-[10px] text-emerald-400">WA ID: {row.extra.wa_id}</div>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
